<template>
    <DCard
        :title="__('customers.business_incidents.connected_activity_logs.title', {numberOfItems: items.length})"
        :main-title-has-slot="true"
        padding
    >
        <template #titleHasSlot>
            <DSwitch
                color="success"
                variant="3d"
                :disabled="isInLoadingState"
                :checked="showCardData"
                @update:checked="showCardSection"
            />
        </template>
        <DRow v-if="showCardData">
            <DCol col="12">
                <section class="activity-logs-table">
                    <div class="table-header">
                        <div>{{ __('customers.business_incidents.connected_activity_logs.description') }}</div>
                        <div>{{ __('customers.business_incidents.connected_activity_logs.subject') }}</div>
                        <div>{{ __('customers.business_incidents.connected_activity_logs.user') }}</div>
                        <div>{{ __('customers.business_incidents.connected_activity_logs.comment') }}</div>
                        <div>{{ __('customers.business_incidents.connected_activity_logs.date') }}</div>
                        <div>{{ __('customers.business_incidents.connected_activity_logs.action') }}</div>
                    </div>
                    <main>
                        <div
                            v-for="(item, index) in items"
                            :key="index"
                            class="item-group"
                        >
                            <div class="item-header">
                                <div>
                                    {{ $page.props.auth.user.language_code === 'de' ? item.activityLog.description : item.activityLog.description_en }}
                                </div>
                                <div v-if="subjectLinkable(item)">
                                    <Link
                                        :href="subjectLink(item)"
                                    >
                                        {{ subjectText(item) }}
                                    </Link>
                                </div>
                                <div v-else>
                                    {{ subjectText(item) }}
                                </div>
                                <div>
                                    {{ item.activityLog.user.name }}
                                </div>
                                <div>
                                    {{ item.activityLog.comment === null ? '' : item.activityLog.comment }}
                                </div>
                                <div>
                                    {{ item.activityLog.created_at }}
                                </div>
                                <div class="action-buttons">
                                    <DButton
                                        v-if="Object.keys(item.activityLog.changes).length > 0"
                                        icon="eye"
                                        mini
                                        @click="viewChanges(item, subjectText(item))"
                                    />
                                    <DButton
                                        v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                        :id="`delete_contract_${item.id}`"
                                        :disabled="isInLoadingState"
                                        icon="trash-can-outline"
                                        color="danger"
                                        mini
                                        @click="showRemoveItem(item)"
                                    />
                                </div>
                            </div>
                        </div>
                    </main>
                </section>
            </DCol>
        </DRow>
        <RemoveItem
            v-if="showRemoveItemModal"
            :show-modal="showRemoveItemModal"
            :title="__('customers.business_incidents.connected_activity_logs.modal.remove.title')"
            :message="__('customers.business_incidents.connected_activity_logs.modal.remove.message', {activityLog: activityLogId})"
            @close-modal="closeRemoveModal"
            @confirm-modal="remove"
        />
        <ChangesModal
            v-if="showChangesModal"
            :show-modal="showChangesModal"
            :changes="changes"
            :subject="subject"
            @close-modal="showChangesModal = false"
        />
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import RemoveItem from '~@/Components/Customers/BusinessIncidents/RemoveItem'
import ChangesModal from '~@/Components/Common/ActivityLogs/ChangesModal'

const props = defineProps({
    items: {
        type: Array,
        default () {
            return []
        }
    },
    incident: {
        type: Object,
        default () {
            return {}
        }
    },
    showCard: {
        type: Boolean,
        default () {
            return true
        }
    }
})
const subjectMapping = {
    Customer: {
        permission: 'customer.view',
        route: 'customers.customers.show',
        parameters: {
            // route parameter name: property name in item
            customer: 'customer_id'
        }
    },
    Invoice: {
        permission: 'invoice.view',
        route: 'customers.customers.invoices.show',
        parameters: {
            customer: 'customer_id',
            invoice: 'subject_id'
        }
    },
    CreditNote: {
        permission: 'credit_note.manage',
        route: 'customers.customers.credit-notes.show',
        parameters: {
            customer: 'customer_id',
            credit_note: 'subject_id'
        }
    },
    Contract: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.index',
        parameters: {
            customer: 'customer_id',
            contract: 'subject_id'
        }
    },
    Contact: {
        permission: 'contact.view',
        route: 'customers.customers.contacts.show',
        parameters: {
            customer: 'customer_id',
            contact: 'subject_id'
        }
    },
    CustomerFile: {
        permission: 'customer.view',
        route: 'customers.customers.customer-files.index',
        parameters: {
            customer: 'customer_id'
        }
    },
    CustomerNote: {
        permission: 'customer.view',
        route: 'customers.customers.customer-notes.index',
        parameters: {
            customer: 'customer_id'
        }
    },
    ContractPhase: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.index',
        parameters: {
            customer: 'customer_id',
            contract: 'contract_phase.contract_id'
        }
    },
    Termination: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.index',
        parameters: {
            customer: 'customer_id',
            contract: 'termination.contract_id'
        }
    },
    Task: {
        permission: 'task.manage',
        route: 'tasks.tasks.index',
        parameters: {
            id: 'subject_id'
        }
    },
    Order: {
        permission: 'order.view',
        route: 'orders.',
        parameters: {
            orderId: 'subject_id'
        }
    },
    User: {
        permission: 'user.view',
        route: 'users.show',
        parameters: {
            user: 'subject_id'
        }
    },
    QueueJobRequest: {
        permission: 'contract.fulfillment',
        route: 'contracts.domains',
        parameters: {
            customerId: 'queue_job_request.job_reference.customer_id'
        }
    },
    Product: {
        permission: 'product.view',
        route: 'products.products.show',
        parameters: {
            product: 'subject_id'
        }
    },
    FulfillmentAccount: {
        permission: 'fulfillment.view',
        route: 'products.accounts.show',
        parameters: {
            account: 'subject_id'
        }
    },
    BusinessIncident: {
        permission: 'customer.incidents_view',
        route: 'customers.customers.business-incidents.index',
        parameters: {
            customer: 'customer_id'
        }
    }
}

const userPermissions = computed(() => usePage().props.auth.permissions)
const showCardData = ref(props.showCard)
const showRemoveItemModal = ref(false)
const showChangesModal = ref(false)
const changes = ref({})
const subject = ref(null)
const activityLogId = ref(null)
const businessIncidentActivityLogId = ref(null)

function showCardSection (value) {
    showCardData.value = value
}

function showRemoveItem (item) {
    businessIncidentActivityLogId.value = item.id
    activityLogId.value = item.activity_log_id
    showRemoveItemModal.value = true
}

function closeRemoveModal () {
    businessIncidentActivityLogId.value = null
    activityLogId.value = null
    showRemoveItemModal.value = false
}

function remove () {
    router.delete(route('customers.customers.delete-business-incident-activity-log', { businessIncidentActivityLog: businessIncidentActivityLogId.value }))
    showRemoveItemModal.value = false
}

function subjectLinkable (item) {
    return item.activityLog.subject_model &&
        subjectMapping[item.activityLog.subject_model.name] !== 'undefined' &&
        areSubjectParamsSet(item, subjectMapping[item.activityLog.subject_model.name].parameters) &&
        userPermissions.value.includes(subjectMapping[item.activityLog.subject_model.name].permission)
}

function subjectLink (item) {
    const subjectMappingElement = subjectMapping[item.activityLog.subject_model.name]
    const routeParameters = {}

    for (const [key, value] of Object.entries(subjectMappingElement.parameters)) {
        routeParameters[key] = getNestedValue(item, value) ?? 0
    }

    if (subjectMappingElement.route === 'tasks.tasks.index') {
        routeParameters.statuses = ['open', 'done']
    }

    return route(subjectMappingElement.route, routeParameters)
}

function subjectText (item) {
    if (item.activityLog.subject_model) {
        if (item.activityLog.subject_model.name === 'Cluster') {
            const clusterId = item.activityLog.properties?.clusterId

            return clusterId && clusterId !== 'N/A'
                ? `${item.activityLog.subject_model.name} (${clusterId})`
                : item.activityLog.subject_model.name
        }

        return `${item.activityLog.subject_model.name} (${item.activityLog.subject_id})`
    }

    return item.activityLog.subject_id
}

function areSubjectParamsSet (item, params) {
    for (const key in params) {
        const pathArray = params[key].split('.')
        let selectedItem = item.activityLog
        for (const path in pathArray) {
            if (selectedItem[pathArray[path]] === null) {
                return false
            }

            selectedItem = selectedItem[pathArray[path]]
        }
    }
    return true
}

function getNestedValue (obj, path) {
    const pathArray = path.split('.')
    let o = obj.activityLog
    for (const p of pathArray) {
        o = o[p]
    }
    return o
}

function viewChanges (item, subjectData) {
    changes.value = item.activityLog.changes
    subject.value = subjectData
    showChangesModal.value = true
}

</script>

<style lang="scss" scoped>
.activity-logs-table {
    & .table-header {
        border-bottom: 1px solid rgba(0,0,0,0.14);
        display: grid;
        grid-template-columns: 17% 17% 17% 17% 17% 15%;
        grid-gap: 10px;
        align-items: center;

        & > div {
            padding: 10px;
            font-weight: bold;
        }
    }
    & main {
        & .item-group {
            border-bottom: 1px solid rgba(0,0,0,0.3);
            position: relative;

            & .item-header {
                display: grid;
                grid-template-columns: 17% 17% 17% 17% 17% 15%;
                grid-gap: 10px;
                background: #fff;
                border-bottom: 1px solid rgba(0,0,0,0.14);
                position: sticky;
                top: -25px;
                width: 100%;
                z-index: 100;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
            }
        }
    }
}
.action-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
}
</style>
